// ToolFilter — the MCP Trust Registry tool-filter wizard (self-hosted analogue
// of the PaaS ToolFilter). Search the registry (multi-result) → select a repo →
// review its tools grouped by classification with risk context → pick an allow-
// or deny-set → BUILD IT INTO A POLICY (writes mcp.servers[server].{allow_list,
// deny_list} — existing fields, no schema change).
//
// Registry Search is fully wired; Local Scan requires a host-side FastMCP scanner
// the self-hosted backend does not expose yet (shown as unavailable).
import { useMemo, useState } from "react";
import { Alert, Button, Card, Checkbox, Flex, Input, Segmented, Select, Spin, Tag, Typography, message, theme } from "antd";
import { Search, ShieldCheck, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { useMcpRegistryApi, type RegistryEvaluation, type RegistryTool } from "@/lib/api/mcpRegistry";
import { useApi, describeError } from "@/lib/api/client";
import { useFetch } from "@/lib/api/useFetch";
import { TOKENS } from "@/lib/chartColors";
import PageHeader from "@/components/PageHeader";

const { Text, Paragraph } = Typography;
const MONO = "var(--font-mono, 'JetBrains Mono Variable', monospace)";
const SERIF = "var(--font-serif, 'Source Serif 4 Variable', serif)";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Policy = { id: string; name: string; version: number; active?: boolean; content: any; description?: string };

// Classification order + tone — mirrors the PaaS TOOL_TYPE_ORDER / TOOL_TYPE_BADGE.
const TYPE_ORDER = ["read-only", "write", "destructive", "admin-config", "exec-capable", "iam-admin", "secrets-access", "local"];
const TYPE_TONE: Record<string, string> = {
  "read-only": TOKENS.governance,
  write: TOKENS.info ?? "#2563eb",
  destructive: TOKENS.danger,
  "admin-config": TOKENS.warning,
  "exec-capable": TOKENS.danger,
  "iam-admin": TOKENS.danger,
  "secrets-access": TOKENS.danger,
  local: TOKENS.info ?? "#2563eb",
};
const RISK_TONE: Record<string, string> = {
  Low: TOKENS.governance,
  Medium: TOKENS.warning,
  High: TOKENS.danger,
  Critical: TOKENS.danger,
};

export default function ToolFilter() {
  const { token } = theme.useToken();
  const api = useApi();
  const { search } = useMcpRegistryApi();

  const { data: policyData } = useFetch<{ items: Policy[] }>((a) => a.GET("/policies", { params: { query: {} } }));
  const policies = policyData?.items ?? [];

  const [q, setQ] = useState("");
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState<RegistryEvaluation[] | null>(null); // multi-result list (>1)
  const [evalu, setEvalu] = useState<RegistryEvaluation | null>(null); // the selected one
  const [serverKey, setServerKey] = useState("");
  const [searchErr, setSearchErr] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [mode, setMode] = useState<"deny" | "allow">("deny");
  const [targetPolicyId, setTargetPolicyId] = useState<string | undefined>();
  const [building, setBuilding] = useState(false);

  const tools = evalu?.tools ?? [];
  const groups = useMemo(() => {
    const m = new Map<string, RegistryTool[]>();
    for (const t of tools) {
      const k = t.type || "other";
      (m.get(k) ?? m.set(k, []).get(k)!).push(t);
    }
    const ordered: [string, RegistryTool[]][] = [];
    for (const k of TYPE_ORDER) if (m.has(k)) ordered.push([k, m.get(k)!]);
    for (const [k, v] of m) if (!TYPE_ORDER.includes(k)) ordered.push([k, v]); // "other" last
    return ordered;
  }, [tools]);

  const pick = (e: RegistryEvaluation) => {
    setResults(null);
    setEvalu(e);
    setServerKey(e.server_name || e.repo_path || "");
    setSelected(new Set());
  };

  const runSearch = async () => {
    const term = q.trim();
    if (!term) return;
    setSearching(true);
    setSearchErr(null);
    setResults(null);
    setEvalu(null);
    try {
      const r = await search(term);
      if (!r.configured) setSearchErr("The MCP Trust Registry is not configured on this console. Contact your administrator.");
      else if (r.count === 0) setSearchErr(`No repositories in the MCP Trust Registry match "${term}".`);
      else if (r.count === 1) pick(r.results[0]);
      else setResults(r.results);
    } catch (e) {
      setSearchErr(e instanceof Error ? e.message : "Registry search failed.");
    } finally {
      setSearching(false);
    }
  };

  const toggle = (name: string) =>
    setSelected((s) => {
      const n = new Set(s);
      n.has(name) ? n.delete(name) : n.add(name);
      return n;
    });
  const toggleGroup = (list: RegistryTool[]) =>
    setSelected((s) => {
      const n = new Set(s);
      const allOn = list.every((t) => n.has(t.name));
      list.forEach((t) => (allOn ? n.delete(t.name) : n.add(t.name)));
      return n;
    });

  const build = async () => {
    const key = serverKey.trim();
    if (!targetPolicyId || !key || selected.size === 0) return;
    const target = policies.find((p) => p.id === targetPolicyId);
    if (!target) return;
    setBuilding(true);
    const picked = tools.filter((t) => selected.has(t.name)).map((t) => t.name);
    const content = JSON.parse(JSON.stringify(target.content ?? {}));
    content.mcp ??= {};
    content.mcp.servers ??= {};
    const prev = content.mcp.servers[key] ?? {};
    content.mcp.servers[key] = {
      ...prev,
      allow_list: mode === "allow" ? picked : [],
      deny_list: mode === "deny" ? picked : [],
      transport: prev.transport ?? (evalu?.transports?.[0] || "stdio"),
    };
    const res = await api.PUT("/policies/{policyId}", {
      params: { path: { policyId: target.id }, header: { "Idempotency-Key": crypto.randomUUID() } },
      body: { description: target.description ?? "", content },
    });
    setBuilding(false);
    if (res.error) {
      message.error(describeError(res.error, res.response.status));
      return;
    }
    message.success(`${mode === "allow" ? "Allow" : "Deny"}-listed ${picked.length} tool${picked.length === 1 ? "" : "s"} on "${key}" in ${target.name} (v${target.version} → v${target.version + 1}).`);
  };

  const resultCard = (r: RegistryEvaluation) => (
    <button
      key={r.repo_path}
      type="button"
      onClick={() => pick(r)}
      style={{ width: "100%", textAlign: "left", padding: "12px 14px", border: `1px solid ${token.colorBorderSecondary}`, borderRadius: 8, background: token.colorBgContainer, cursor: "pointer" }}
    >
      <div style={{ fontFamily: MONO, fontSize: 13, fontWeight: 600, color: token.colorText }}>{r.repo_path}</div>
      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 10, marginTop: 4 }}>
        {r.risk && <Tag color={RISK_TONE[r.risk] ?? undefined} style={{ marginInlineEnd: 0 }}>{r.risk}</Tag>}
        {r.org_type && <span style={{ fontSize: 12, color: token.colorTextTertiary }}>{r.org_type}</span>}
        <span style={{ fontSize: 12, color: token.colorTextTertiary }}>{r.num_tools ?? r.tools?.length ?? 0} tools</span>
        <span style={{ fontSize: 12, color: token.colorTextTertiary }}>{r.num_findings ?? 0} findings</span>
      </div>
    </button>
  );

  return (
    <Flex vertical gap="middle">
      <PageHeader
        title="MCP Tool Filter"
        subtitle="Search the MCP Trust Registry for a repository, review its tools, and build an allow/deny policy for its server."
      />

      <Card size="small" styles={{ body: { padding: 16 } }}>
        <div style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5, color: token.colorTextTertiary, marginBottom: 8 }}>
          Registry Search
        </div>
        <Text type="secondary" style={{ display: "block", fontSize: 13, marginBottom: 10 }}>
          Search the MCP Trust Registry for a repository to view and filter its tools.
        </Text>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <Input
            style={{ maxWidth: 460, fontFamily: MONO }}
            placeholder="e.g. servicenow  or  echelon-ai-labs/servicenow-mcp"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onPressEnter={runSearch}
            prefix={<Search size={14} style={{ color: token.colorTextTertiary }} />}
          />
          <Button type="primary" onClick={runSearch} loading={searching} disabled={!q.trim()}>Search Registry</Button>
        </div>
        {searchErr && <Alert style={{ marginTop: 12 }} type="warning" showIcon message={searchErr} />}

        {/* multi-result selection */}
        {results && results.length > 1 && !evalu && (
          <div style={{ marginTop: 14 }}>
            <Text type="secondary" style={{ fontSize: 13 }}>{results.length} repositories found. Select one:</Text>
            <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 8 }}>{results.map(resultCard)}</div>
          </div>
        )}
      </Card>

      {searching && <Spin style={{ margin: "24px auto" }} />}

      {/* selected evaluation → tool selection */}
      {evalu && (
        <Card size="small" styles={{ body: { padding: 16 } }}>
          <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 10 }}>
            <ShieldCheck size={16} style={{ color: TOKENS.governance }} />
            <span style={{ fontFamily: SERIF, fontSize: 16, fontWeight: 600 }}>{evalu.server_name || evalu.repo_path}</span>
            {evalu.risk && <Tag color={RISK_TONE[evalu.risk] ?? undefined}>{evalu.risk} risk</Tag>}
            {evalu.org_type && <span style={{ fontSize: 12, color: token.colorTextTertiary }}>{evalu.org_type}</span>}
            <span style={{ marginLeft: "auto", fontSize: 12, color: token.colorTextTertiary, fontFamily: MONO }}>
              {evalu.num_tools ?? tools.length} tools · {evalu.num_findings ?? 0} findings
            </span>
          </div>
          <Text type="secondary" style={{ fontSize: 12, fontFamily: MONO }}>
            {evalu.repo_path}{evalu.resolved_version ? ` @ ${evalu.resolved_version}` : ""}
          </Text>
          {results === null && evalu && (
            <Button type="link" size="small" style={{ padding: 0, height: "auto", marginLeft: 8, fontSize: 12 }} onClick={() => { setEvalu(null); runSearch(); }}>← back to results</Button>
          )}
          {evalu.risk_assessment && <Paragraph type="secondary" style={{ fontSize: 12.5, marginTop: 8, marginBottom: 0 }}>{evalu.risk_assessment}</Paragraph>}

          {/* server name (editable — the policy key) */}
          <div style={{ marginTop: 14 }}>
            <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5, color: token.colorTextTertiary, marginBottom: 4 }}>Server name (policy key)</div>
            <Input style={{ maxWidth: 320, fontFamily: MONO }} size="small" value={serverKey} onChange={(e) => setServerKey(e.target.value)} placeholder="ServiceNow" />
          </div>

          {/* mode + select all */}
          <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 12, margin: "14px 0 6px" }}>
            <Segmented size="small" value={mode} onChange={(v) => setMode(v as "deny" | "allow")}
              options={[{ label: "Deny selected", value: "deny" }, { label: "Allow only selected", value: "allow" }]} />
            <span style={{ fontSize: 12, color: token.colorTextSecondary }}>
              {mode === "deny" ? "Checked tools are blocked; everything else stays callable." : "Only checked tools are callable; everything else is blocked."}
            </span>
            <Button size="small" type="text" onClick={() => setSelected(new Set(tools.map((t) => t.name)))}>Select all</Button>
            <Button size="small" type="text" onClick={() => setSelected(new Set())}>Clear</Button>
          </div>

          {/* tool groups (PaaS order) */}
          <div style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: 8 }}>
            {groups.map(([type, list]) => {
              const allOn = list.every((t) => selected.has(t.name));
              const someOn = !allOn && list.some((t) => selected.has(t.name));
              return (
                <div key={type} style={{ border: `1px solid ${token.colorBorderSecondary}`, borderRadius: 8, overflow: "hidden" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 12px", background: token.colorFillQuaternary }}>
                    <Checkbox checked={allOn} indeterminate={someOn} onChange={() => toggleGroup(list)} />
                    <Tag color={TYPE_TONE[type] ?? undefined} style={{ fontFamily: MONO, marginInlineEnd: 0 }}>{type}</Tag>
                    <span style={{ fontSize: 12, color: token.colorTextTertiary }}>{list.length}</span>
                  </div>
                  <div style={{ padding: "4px 12px 8px" }}>
                    {list.map((t) => (
                      <div key={t.name} style={{ display: "flex", alignItems: "flex-start", gap: 8, padding: "5px 0" }}>
                        <Checkbox checked={selected.has(t.name)} onChange={() => toggle(t.name)} />
                        <div style={{ minWidth: 0 }}>
                          <span style={{ fontFamily: MONO, fontSize: 12.5 }}>{t.name}</span>
                          {t.description && <div style={{ fontSize: 11.5, color: token.colorTextTertiary }}>{t.description}</div>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>

          {/* build into policy */}
          <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 10, marginTop: 16, paddingTop: 14, borderTop: `1px solid ${token.colorBorderSecondary}` }}>
            <span style={{ fontSize: 12, color: token.colorTextSecondary }}>Build into policy:</span>
            <Select size="small" style={{ minWidth: 220 }} placeholder="Select a policy" value={targetPolicyId} onChange={setTargetPolicyId}
              options={policies.map((p) => ({ value: p.id, label: `${p.name} (v${p.version})${p.active ? " · active" : ""}` }))} />
            <Button type="primary" icon={<ArrowRight size={14} />} disabled={!targetPolicyId || selected.size === 0 || !serverKey.trim()} loading={building} onClick={build}>
              Apply {selected.size} tool{selected.size === 1 ? "" : "s"} → {mode === "allow" ? "allow" : "deny"}-list
            </Button>
            <Link to="/policy" style={{ fontSize: 12 }}>Open Policy →</Link>
          </div>
          <Text type="secondary" style={{ fontSize: 11, display: "block", marginTop: 8 }}>
            Writes <code style={{ fontFamily: MONO }}>mcp.servers["{serverKey || "…"}"].{mode === "allow" ? "allow_list" : "deny_list"}</code> and bumps the policy version. Saving re-deploys to every sandbox on that policy.
          </Text>
        </Card>
      )}
    </Flex>
  );
}
